import React, { useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  Button,
  List,
  ListItem,
  ListItemText,
  Badge
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { removeFromCart, updateQuantity, setCartOpen, clearCart } from '../../redux/slices/catalogCartSlice';
import { createOrder } from '../../api/orders';
import { formatDateForOrder, getInputDate } from '../../utils/dates';

export const CartDrawer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id: supplierId } = useParams();
  const { items, isOpen } = useSelector((state) => state.catalogCart);
  const auth = useSelector((state) => state?.auth);
  const token = auth?.token || null;
  const restaurant = auth?.restaurant || null;
  const user = auth?.user || null;
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  const subtotal = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const iva = items.reduce((sum, item) => {
    const itemSubtotal = item.price * item.quantity;
    const itemIva = itemSubtotal * ((item.iva || 0) / 100);
    return sum + itemIva;
  }, 0);
  const total = subtotal + iva;

  const handleClose = () => {
    dispatch(setCartOpen(false));
  };

  const handleRemove = (id) => {
    dispatch(removeFromCart(id));
  };

  const handleIncrement = (id, currentQuantity) => {
    dispatch(updateQuantity({ id, quantity: currentQuantity + 1 }));
  };

  const handleDecrement = (id, currentQuantity) => {
    if (currentQuantity > 1) {
      dispatch(updateQuantity({ id, quantity: currentQuantity - 1 }));
    } else {
      dispatch(removeFromCart(id));
    }
  };

  const handleRealizarPedido = async () => {
    if (items.length === 0) {
      toast.error('El carrito está vacío');
      return;
    }

    if (!token || !restaurant || !user) {
      dispatch(setCartOpen(false));
      sessionStorage.setItem('redirectAfterLogin', window.location.pathname);
      navigate('/ingreso');
      return;
    }

    if (!supplierId) {
      toast.error('Información de sesión incompleta');
      return;
    }

    setIsSubmitting(true);

    try {
      const scheduleDate = formatDateForOrder(getInputDate());
      
      const products = items.map(item => ({
        id: item.id,
        name: item.name,
        price: item.price,
        iva: item.iva || 0,
        quantity: item.quantity,
        requestedQuantity: item.quantity,
        measurementUnit: item.measurementUnit,
        image: item.image
      }));

      const order = {
        supplierId: parseInt(supplierId),
        products: products,
        scheduleDate,
        note: '',
        restaurantId: parseInt(restaurant),
        subtotal: parseFloat(subtotal.toFixed(2)),
        iva: parseFloat(iva.toFixed(2)),
        total: parseFloat(total.toFixed(2)),
        createdBy: user?.id
      };

      const orderResponse = await createOrder(order, token, restaurant, user?.id);
      
      toast.success('¡Orden creada exitosamente!');
      
      dispatch(clearCart());
      dispatch(setCartOpen(false));
      
      navigate(`/orders`);
      
    } catch (error) {
      console.error('Error creating order:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('No se pudo crear la orden. Inténtelo más tarde');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={handleClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 400 },
          boxSizing: 'border-box'
        }
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 2,
            backgroundColor: '#49a35b',
            color: '#fff'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShoppingCartIcon />
            <Typography variant="h6" fontWeight="bold">
              Carrito
            </Typography>
            <Badge sx={{marginLeft: '9px'}} badgeContent={items.length} color="error" />
          </Box>
          <IconButton onClick={handleClose} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Cart Items */}
        <Box sx={{ flex: 1, overflowY: 'auto', padding: 2 }}>
          {items.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                gap: 2
              }}
            >
              <ShoppingCartIcon sx={{ fontSize: 80, color: '#ccc' }} />
              <Typography variant="body1" color="text.secondary" textAlign="center">
                Tu carrito está vacío
              </Typography>
            </Box>
          ) : (
            <List sx={{ padding: 0 }}>
              {items.map((item) => (
                <React.Fragment key={item.id}>
                  <ListItem
                    sx={{
                      padding: 2,
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: 1
                    }}
                  >
                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <ListItemText
                        primary={
                          <Typography variant="body1" fontWeight="bold" sx={{ fontSize: '14px' }}>
                            {item.name}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            ${item.price} / {item.measurementUnit || 'unidad'}
                          </Typography>
                        }
                      />
                      <IconButton
                        size="small"
                        onClick={() => handleRemove(item.id)}
                        sx={{ color: '#f44336' }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    <Box
                      sx={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleDecrement(item.id, item.quantity)}
                          sx={{
                            backgroundColor: '#f0f0f0',
                            width: 28,
                            height: 28,
                            '&:hover': { backgroundColor: '#e0e0e0' }
                          }}
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                        <Typography variant="body1" sx={{ minWidth: 30, textAlign: 'center', fontWeight: 'bold' }}>
                          {item.quantity}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => handleIncrement(item.id, item.quantity)}
                          sx={{
                            backgroundColor: '#49a35b',
                            color: '#fff',
                            width: 28,
                            height: 28,
                            '&:hover': { backgroundColor: '#3d8a4d' }
                          }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <Typography variant="body1" fontWeight="bold" color="#49a35b">
                        ${(item.price * item.quantity).toFixed(2)}
                      </Typography>
                    </Box>
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>

        {/* Footer Summary */}
        {items.length > 0 && (
          <Box sx={{ padding: 2, backgroundColor: '#f8f9fa', borderTop: '1px solid #ddd' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 1 }}>
              <Typography variant="body1">Subtotal:</Typography>
              <Typography variant="body1" fontWeight="bold">
                ${subtotal.toFixed(2)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 1 }}>
              <Typography variant="body1">IVA:</Typography>
              <Typography variant="body1" fontWeight="bold">
                ${iva.toFixed(2)}
              </Typography>
            </Box>
            <Divider sx={{ marginY: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
              <Typography variant="h6" fontWeight="bold">
                Total:
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="#49a35b">
                ${total.toFixed(2)}
              </Typography>
            </Box>
            <Button
              variant="contained"
              fullWidth
              disabled={isSubmitting}
              onClick={handleRealizarPedido}
              sx={{
                backgroundColor: '#49a35b',
                color: '#fff',
                fontWeight: 'bold',
                padding: 1.5,
                '&:hover': {
                  backgroundColor: '#3d8a4d'
                },
                '&.Mui-disabled': {
                  backgroundColor: '#ccc',
                  color: '#999'
                }
              }}
            >
              {isSubmitting ? 'Procesando...' : 'Realizar Pedido'}
            </Button>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};
